import { useEffect, useState } from "react";
import { Link2, Save, PlugZap, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Field, SimpleSelect } from "@/components/wizard/Field";

export const FoundeeSettings = ({ admin }) => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ email: "", password: "", default_domain: "__default__", auto_create: true });
  const [domains, setDomains] = useState([]);
  const [testing, setTesting] = useState(false);

  const apply = (data) => { setS(data); setF((x) => ({ ...x, email: data.email || "", default_domain: data.default_domain || "__default__", auto_create: data.auto_create })); };
  useEffect(() => {
    api.get("/settings/foundee").then((r) => apply(r.data)).catch(() => {});
    api.get("/smartlinks/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const body = { email: f.email, default_domain: f.default_domain === "__default__" ? "" : f.default_domain, auto_create: f.auto_create };
      if (f.password) body.password = f.password;
      const { data } = await api.put("/settings/foundee", body);
      apply(data); setF((x) => ({ ...x, password: "" })); toast.success("Ajustes de smartlinks guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/foundee/test");
      setDomains(data.domains.filter((d) => d.status === "CONFIRMED"));
      toast.success(`Conexión correcta · ${data.domains.length} dominios`);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  const options = [["__default__", "found.ee (por defecto)"], ...domains.map((d) => [d.domain, d.domain])];
  if (f.default_domain !== "__default__" && !domains.some((d) => d.domain === f.default_domain)) options.push([f.default_domain, f.default_domain]);

  return (
    <section className="lg:col-span-7 panel p-6 sm:p-8 space-y-5 rise" data-testid="foundee-settings">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center shrink-0"><Link2 className="h-5 w-5" /></div>
        <div><h2 className="font-display text-lg font-semibold">Smartlinks (Found.ee)</h2><p className="text-sm text-muted-foreground">Crea páginas pre-save y de lanzamiento con tus dominios personalizados. Credenciales cifradas; los artistas solo ven el enlace final.</p></div>
      </div>
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="foundee-status">
          {[["Cuenta", s.email ? "configurada" : "—"], ["Contraseña", s.has_password ? "guardada" : "—"], ["Último login OK", s.last_login_ok_at ? new Date(s.last_login_ok_at).toLocaleDateString() : "nunca"], ["Dominios", domains.length || "—"]].map(([k, v]) => (
            <div key={k} className="rounded-md border border-border p-3"><p className="eyebrow">{k}</p><p className="font-medium mt-1">{v}</p></div>
          ))}
        </div>
      )}
      {s?.last_error && <p className="text-xs text-destructive" data-testid="foundee-last-error">{s.last_error}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Correo de Found.ee"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} disabled={!admin} data-testid="foundee-email-input" /></Field>
        <Field label="Contraseña de Found.ee" hint={s?.has_password ? "Déjalo vacío para conservar la actual" : ""}><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} disabled={!admin} autoComplete="new-password" data-testid="foundee-password-input" /></Field>
      </div>
      <Field label="Dominio por defecto" hint="Se usa cuando la cuenta del artista no tiene un dominio propio asignado en Equipo.">
        <SimpleSelect value={f.default_domain} onChange={(v) => setF({ ...f, default_domain: v })} options={options} testId="foundee-domain-select" />
      </Field>
      <div className="flex items-center justify-between rounded-md border border-border p-4">
        <div className="text-sm"><p className="font-medium">Crear smartlink automáticamente</p><p className="text-xs text-muted-foreground">En cuanto un lanzamiento recibe su UPC: pre-save si la fecha es futura (se convierte en página normal el día del estreno) o página de lanzamiento si ya salió.</p></div>
        <Switch checked={f.auto_create} onCheckedChange={(v) => setF({ ...f, auto_create: v })} disabled={!admin} data-testid="foundee-auto-switch" />
      </div>
      <div className="flex flex-wrap gap-2">
        {admin && <Button onClick={save} className="gap-2" data-testid="btn-save-foundee"><Save className="h-4 w-4" /> Guardar</Button>}
        <Button variant="outline" onClick={test} disabled={testing} className="gap-2" data-testid="btn-test-foundee"><PlugZap className="h-4 w-4" /> {testing ? "Probando…" : "Probar conexión"}</Button>
        {!admin && <p className="text-xs text-muted-foreground flex items-center gap-1 self-center"><ShieldAlert className="h-3.5 w-3.5" /> Solo un administrador puede modificar estos ajustes.</p>}
      </div>
    </section>
  );
};
